import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import Page from "src/components/Page";
import { BaseTable } from "src/components/BaseTable/BaseTable";
import { payoutRunColumns } from "./payoutHistory.constants";
import { fetchWithError } from "src/react-query/fetchWithError";
import { selector } from "src/react-query/select";
import { queryKeys } from "src/react-query/queryKeys";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";

const SummaryCard = ({ title, value, color = "text.primary" }) => (
  <Paper elevation={2} sx={{ p: 2, textAlign: "center", minWidth: 140 }}>
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {title}
    </Typography>
    <Typography variant="h5" color={color} fontWeight="bold">
      {value}
    </Typography>
  </Paper>
);

const PayoutRunHistory = () => {
  const navigate = useNavigate();
  const team = useSelectedTeam();
  const companyId = team?.teamId;
  const companyType = team?.teamType; // "P" for parent/root, "T" for team
  const [statusFilter, setStatusFilter] = useState("");
  const [daysFilter, setDaysFilter] = useState(7);
  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const params = useMemo(() => {
    const p = { limit: pageSize, page };
    if (statusFilter) p.status = statusFilter;
    if (daysFilter) p.days = daysFilter;
    return p;
  }, [statusFilter, companyType, daysFilter, page, pageSize]);

  const {
    data: runHistory,
    isLoading: historyLoading,
    isFetched: historyFetched,
  } = useQuery(
    queryKeys.payoutRunHistory(null, companyId, params),
    fetchWithError,
    {
      enabled: !!companyId,
     // select: selector,
      retry: false,
    }
  );
  const columns = useMemo(() => payoutRunColumns(navigate), [navigate]);

  const rows = useMemo(() => {
    if (!runHistory) return [];
    const list = Array.isArray(runHistory) ? runHistory : runHistory?.data || [];
    return list.map((row) => ({ ...row, id: row.id }));
  }, [runHistory]);

  const rowClickHandler = ({ id, field }) => {
    if (field !== "__check__") {
      navigate(`/payout-history/${id}`);
    }
  };

  const isLoading = historyLoading && !!companyId;
  const hasNoData = historyFetched && rows.length === 0 && !historyLoading;

  return (
    <Page title="Payout Run History | Smart-CMS">
      <Typography variant="h4" gutterBottom>
        Payout Run History
        {team?.teamName && (
          <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 2 }}>
            — {team.teamName}
          </Typography>
        )}
      </Typography>

      {!companyId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Please select a team to view payout run history.
        </Alert>
      )}

      {historyFetched && runHistory?.summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3} md={2}>
            <SummaryCard title="Total Runs" value={runHistory?.summary?.totalRuns || 0} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <SummaryCard
              title="Successful"
              value={runHistory?.summary?.successful || 0}
              color="success.main"
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <SummaryCard
              title="Failed"
              value={runHistory?.summary?.failed || 0}
              color="error.main"
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <SummaryCard
              title="Total Payout"
              value={Number(runHistory?.summary?.totalPayoutAmount || 0).toFixed(2)}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <SummaryCard
              title="Zynka Commission"
              value={Number(runHistory?.summary?.totalZynkaCommission || 0).toFixed(2)}
              color="info.main"
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <SummaryCard
              title="Transactions"
              value={runHistory?.summary?.totalTransactions || 0}
            />
          </Grid>
        </Grid>
      )}

      <Card sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="SUCCESS">Success</MenuItem>
            <MenuItem value="FAILED">Failed</MenuItem>
            <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
          </TextField>
          <TextField
            select
            label="Summary Period"
            value={daysFilter}
            onChange={(e) => setDaysFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 160 }}
          >
            <MenuItem value={7}>Last 7 Days</MenuItem>
            <MenuItem value={14}>Last 14 Days</MenuItem>
            <MenuItem value={30}>Last 30 Days</MenuItem>
            <MenuItem value={90}>Last 90 Days</MenuItem>
          </TextField>
        </Stack>

        {hasNoData && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No payout runs found. Payout runs will appear here after the scheduler executes or a manual payout is triggered.
          </Alert>
        )}

        <BaseTable
            rows={rows}
            columns={columns}
            loading={isLoading}
            getRowId={(row) => row.id}
            page={page}
            pageSize={pageSize}
            rowCount={runHistory?.total || (Array.isArray(runHistory) ? runHistory.length : 0)}
            onPageChange={(newPage) => setPage(Number(newPage) || 0)}
            onPageSizeChange={(newPageSize) => setPageSize(Number(newPageSize) || 10)}
            rowOrCellClickRedirect={rowClickHandler}
            disableSelectionOnClick={true}
            serverRowFetcher={true}
            pagination={true}
            rowsPerPageOptions={[10, 20, 30, 50]}
        />
      </Card>
    </Page>
  );
};

export default PayoutRunHistory;
